<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, onBeforeUnmount } from 'vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import type { TMenuSidePanelTab } from '@/composition/menu-import/menu-import-context';
  import type { IListReveal } from '@/domain/menu-import/assistant-view';
  import { MENU_IMPORT_BOXES_ENABLED } from '@/domain/menu-import/source-viewer';
  import V2MenuAssistant from './V2MenuAssistant.vue';
  import V2MenuSourceViewer from './V2MenuSourceViewer.vue';

  /**
   * The workspace's side panel (plan §4.5, §4.6, §4.5.5): «Pedir cambios» |
   * «Carta original». One node: the workspace docks it as the right pane on
   * wide screens and puts it in the bottom sheet on narrow ones. Both panes
   * stay mounted (`v-show`), so switching tabs never loses a typed message.
   */
  const props = defineProps<{
    tab: TMenuSidePanelTab;
    /** A read runs: the chat waits for it. */
    readingActive: boolean;
    wide: boolean;
    /** The store's country (price formats). */
    country: string;
  }>();
  const emit = defineEmits<{
    (e: 'tab', tab: TMenuSidePanelTab): void;
    /** Narrow screens («Ver cambios», a box tapped): where the list should scroll, above the lowered sheet. */
    (e: 'reveal', target: IListReveal): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const ctx = useMenuImport();
  let alive = true;

  const tabs = computed(() => [
    { id: 'chat', label: String(proxy.$t('menuImport.chat.title')) },
    { id: 'original', label: String(proxy.$t('menuImport.original')) },
  ]);
  /** The panel is on screen: docked on wide screens outside a read, while its sheet is open on narrow ones. */
  const onScreen = computed(() =>
    props.wide ? !props.readingActive : ctx.sheetOpen.value
  );
  const chatActive = computed(() => props.tab === 'chat' && onScreen.value);
  const pages = computed(() => ctx.pages.value);
  const draft = computed(() => ctx.draft.value);
  const selectedItemId = computed(() => ctx.selectedItemId.value);
  const viewerPageId = computed(() => ctx.viewerPageId.value);

  function isSidePanelTab(value: string): value is TMenuSidePanelTab {
    return value === 'chat' || value === 'original';
  }

  function onTab(value: string) {
    if (isSidePanelTab(value) && value !== props.tab) emit('tab', value);
  }

  /**
   * A box tapped in «Carta original»: that dish is selected in the list
   * (scrolled to and flashed, even when it already was). On narrow screens
   * the sheet goes down to 40 % so the list shows.
   */
  async function selectItem(itemId: string) {
    if (!props.wide) ctx.sheetHeight.value = 'sm';
    ctx.selectedItemId.value = null;
    await nextTick();
    if (!alive) return;
    ctx.selectedItemId.value = itemId;
    // Narrow: the row goes to the top of the list, clear of the lowered sheet.
    const item = ctx.draft.value?.items[itemId];
    if (!props.wide && item)
      emit('reveal', { itemId: item.id, categoryId: item.categoryId });
  }

  function onPage(pageId: string) {
    ctx.viewerPageId.value = pageId;
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-side-panel">
    <header class="side-head">
      <PosUiSegmented
        class="side-tabs"
        block
        :value="tab"
        :items="tabs"
        @input="onTab"
      />
    </header>
    <V2MenuAssistant
      v-show="tab === 'chat'"
      class="side-pane"
      :disabled="readingActive"
      :wide="wide"
      :country="country"
      :active="chatActive"
      @reveal="emit('reveal', $event)"
    />
    <V2MenuSourceViewer
      v-show="tab === 'original'"
      class="side-pane"
      :pages="pages"
      :draft="draft"
      :selected-item-id="selectedItemId"
      :focus-page-id="viewerPageId"
      :boxes="MENU_IMPORT_BOXES_ENABLED"
      @select-item="selectItem"
      @page="onPage"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-side-panel {
    @apply tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-bg-white;
    .side-head {
      @apply tw-flex tw-shrink-0 tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-4 tw-py-2.5;
    }
    .side-tabs {
      @apply tw-w-full;
    }
    .side-pane {
      @apply tw-min-h-0 tw-flex-1;
    }
  }
</style>
